import { useBloc } from "@blac/react";
import Chart from "molecule/chart/Chart";
import React, { FC, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import ChartEmptyState from "./ChartEmptyState";
import DataHubCubit from "./DataHubCubit";
import { LoincCodingCode } from "src/constants/fhir";
import { globalEvents } from "src/constants/globalEvents";
import ChartLoadingState from "./ChartLoadingState";
import ChartComingSoon from "./ChartComingSoon";
import ChartCustomizedRectangle, {
  Series
} from "molecule/chart/ChartCustomizedRectangle";
import { buildChartConfig, getYAxisTickFormatter } from "./DataHubChartHelpers";

const DataHubChart: FC = () => {
  const navigate = useNavigate();
  const [
    { activeLoincCode, chartData, chartLabel, activeTimeRange },
    {
      tickFormatterAxisX,
      formatLabel,
      formatValue,
      availableCharts,
      clearApiCache,
      setActiveObservations,
      refreshImportedObservation,
      formatValueRemoveZeroDecimals
    }
  ] = useBloc(DataHubCubit);
  const isAvailable = activeLoincCode
    ? availableCharts.includes(activeLoincCode)
    : false;

  const handleReload = useCallback(() => {
    clearApiCache(activeLoincCode);
    void setActiveObservations({
      loincCode: activeLoincCode,
      forceRefresh: true
    });
  }, [activeLoincCode, setActiveObservations]);

  const type = useMemo(
    () => new URLSearchParams(window.location.search).get("type"),
    []
  );

  useEffect(() => {
    if (type) {
      void setActiveObservations({
        loincCode: type as LoincCodingCode,
        forceRefresh: true
      });
    }
  }, [type, setActiveObservations]);

  useEffect(() => {
    if (activeLoincCode) {
      const searchParams = new URLSearchParams(window.location.search);
      searchParams.set("type", activeLoincCode);
      navigate(`?${searchParams.toString()}`, { replace: true });
    }
  }, [activeLoincCode, navigate]);

  useEffect(() => {
    const handleDataSubmitted = (event: Event) => {
      const customEvent = event as CustomEvent<{ loincCode?: LoincCodingCode }>;
      const submittedLoincCode = customEvent.detail.loincCode;

      // If a loinc code was provided, switch to that chart
      if (submittedLoincCode) {
        void setActiveObservations({
          loincCode: submittedLoincCode,
          forceRefresh: true
        });
      } else {
        handleReload();
      }
    };

    document.addEventListener(globalEvents.DATA_SUBMITTED, handleDataSubmitted);

    return () => {
      document.removeEventListener(
        globalEvents.DATA_SUBMITTED,
        handleDataSubmitted
      );
    };
  }, [handleReload, setActiveObservations]);

  useEffect(() => {
    const handleObservationImported = (event: Event) => {
      const customEvent = event as CustomEvent<{ loincCode: string }>;

      void refreshImportedObservation(
        customEvent.detail.loincCode as LoincCodingCode
      );
    };

    document.addEventListener(
      globalEvents.OBSERVATION_IMPORTED,
      handleObservationImported
    );

    return () => {
      document.removeEventListener(
        globalEvents.OBSERVATION_IMPORTED,
        handleObservationImported
      );
    };
  }, [refreshImportedObservation]);

  const defaultXAxis: React.ComponentProps<typeof Chart>["xAxis"] = {
    type: "number",
    scale: "time",
    tickFormatter: (time: number) => tickFormatterAxisX(time),
    ticks: chartLabel?.ticks,
    domain: chartLabel?.domain,
    dataKey: "time"
  };

  // loading state for chart
  if (isAvailable && typeof chartData === "undefined") {
    return <ChartLoadingState />;
  }

  if (!isAvailable) {
    return <ChartComingSoon />;
  }

  if (!chartData || chartData.length === 0) {
    return <ChartEmptyState />;
  }

  const chartConfig = buildChartConfig(activeLoincCode);

  if (!chartConfig) {
    return null;
  }

  const yAxisTickFormatter = getYAxisTickFormatter(
    activeLoincCode,
    formatValueRemoveZeroDecimals
  );

  const isBloodGlucoseChart =
    activeLoincCode === LoincCodingCode.bloodGlucoseCapillary;

  return (
    <Chart
      {...chartConfig}
      xAxis={defaultXAxis}
      yAxis={{
        ...chartConfig.yAxis,
        tickFormatter: yAxisTickFormatter
      }}
      customized={
        isBloodGlucoseChart
          ? (props) => (
              <ChartCustomizedRectangle
                formattedGraphicalItems={
                  props.formattedGraphicalItems as Series[]
                }
                timeRange={
                  activeTimeRange?.domain.days === 1 ? "1d" : undefined
                }
              />
            )
          : undefined
      }
      chartData={chartData}
      tooltip={{
        formatLabel,
        formatValue
      }}
    />
  );
};
export default DataHubChart;
